/**
 * Shared certificate "certified units" table row building (modal + PDF).
 * Legacy JSON (no nfryTokens): NFRY ids come from entitlement credit bundle details
 * for the same FinalToken id only; rows are zipped in order with assets[].yields[].
 */

export function formatCertificateTableDate(date) {
  if (!date) return "-";
  const timestamp = date?.$date ? date.$date : date;
  if (!timestamp) return "-";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  try {
    return new Date(timestamp).toLocaleDateString("en-US", options);
  } catch {
    return "-";
  }
}

export function resolveTokenPeriod(token, certificateData) {
  if (token?.period != null && String(token.period).trim() !== "") {
    return token.period;
  }

  const periods = new Set();
  const assets = Array.isArray(certificateData?.assets) ? certificateData.assets : [];

  for (const asset of assets) {
    const yields = Array.isArray(asset?.yields) ? asset.yields : [];
    for (const yieldData of yields) {
      const period = yieldData?.yield_period;
      if (period != null && String(period).trim() !== "") {
        periods.add(String(period).trim());
      }
    }
  }

  return periods.size === 1 ? Array.from(periods)[0] : "-";
}

export function sumBundledCreditsFromLinkageRows(bundleDetailsRows) {
  const rows = Array.isArray(bundleDetailsRows) ? bundleDetailsRows : [];
  const total = rows.reduce((sum, row) => {
    const value = Number(row?.no_of_entitlement_records);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);
  return total > 0 ? total : null;
}

export function resolveBundledCreditsTotal(certificateData, bundleDetailsRows) {
  const linkedTotal = sumBundledCreditsFromLinkageRows(bundleDetailsRows);
  if (linkedTotal != null) {
    return linkedTotal;
  }
  const fallback = Number(certificateData?.amount_of_tradable_certificates);
  return Number.isFinite(fallback) ? fallback : null;
}

export function formatCreditValue(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }
  return parsed.toFixed(3);
}

/**
 * @param {object} certificateData - single certificate object from final_certificate_json
 * @param {unknown} bundleDetailsRows - rows enriched from backend yield response
 */
export function buildCertificateUnitRows(certificateData, bundleDetailsRows, options = {}) {
  const certificateType = options?.certificateType === "retirement" ? "retirement" : "standard";
  const bundles = Array.isArray(bundleDetailsRows) ? bundleDetailsRows : [];
  const isPresent = (value) => value != null && String(value).trim() !== "";
  const firstPresent = (...values) => values.find((value) => isPresent(value));
  const asTon = (value) =>
    isPresent(value)
      ? typeof value === "string"
        ? `${value}`
        : `${Number(value).toFixed(2)} ton`
      : "-";
  const asTco2 = (value) =>
    isPresent(value) ? `${Number(value).toFixed(3)} tCO2e` : "-";
  const totalTradableCertificates = isPresent(certificateData?.amount_of_tradable_certificates)
    ? Number(certificateData.amount_of_tradable_certificates)
    : null;

  const computeFallbackBundledAmount = (realizedValue, totalRealizedValue) => {
    if (
      !isPresent(realizedValue) ||
      totalTradableCertificates == null ||
      !Number.isFinite(totalTradableCertificates) ||
      !isPresent(totalRealizedValue)
    ) {
      return realizedValue;
    }

    const realizedNumber = Number(realizedValue);
    const totalRealizedNumber = Number(totalRealizedValue);
    if (!Number.isFinite(realizedNumber) || !Number.isFinite(totalRealizedNumber)) {
      return realizedValue;
    }
    if (totalRealizedNumber <= 0) {
      return realizedValue;
    }

    return (realizedNumber / totalRealizedNumber) * totalTradableCertificates;
  };

  const findBundleByTokenId = (tokenId, idx) => {
    if (!isPresent(tokenId)) return bundles[idx] || null;
    return (
      bundles.find(
        (bundle) =>
          String(bundle?.yield_token_id ?? "").trim() === String(tokenId).trim()
      ) || bundles[idx] || null
    );
  };

  const nfryTokens = Array.isArray(certificateData?.nfryTokens)
    ? certificateData.nfryTokens
    : [];
  if (nfryTokens.length > 0) {
    return nfryTokens.map((token, idx) => {
      const backend = findBundleByTokenId(token?.tokenId, idx);
      const tokenId = firstPresent(token?.tokenId, backend?.yield_token_id);
      const period = firstPresent(
        token?.period,
        backend?.period,
        resolveTokenPeriod(token, certificateData)
      );
      const issuanceDate = firstPresent(token?.issuanceDate, backend?.created);
      const assetId = firstPresent(
        token?.externalAssetId,
        token?.assetId,
        backend?.external_asset_id,
        backend?.asset_id
      );
      const productVolume = firstPresent(
        token?.productVolume,
        backend?.yield_product_volume
      );
      const realizedYield = firstPresent(token?.realizedYield, backend?.co2_realized);
      const bundledAmount = firstPresent(
        token?.creditsIncluded,
        certificateType === "retirement"
          ? backend?.claimed_range_credits
          : backend?.no_of_entitlement_records,
        backend?.no_of_entitlement_records,
        backend?.co2_realized
      );

      return {
        key: `token-${idx}`,
        tokenId: isPresent(tokenId) ? `NFRY-${String(tokenId).trim()}` : "-",
        issuanceDate: isPresent(issuanceDate)
          ? formatCertificateTableDate(issuanceDate)
          : "-",
        assetId: isPresent(assetId) ? String(assetId) : "-",
        period: isPresent(period) ? String(period) : "-",
        productVolume: asTon(productVolume),
        yield: asTco2(realizedYield),
        bundledAmount: asTco2(bundledAmount),
      };
    });
  }

  const assets = Array.isArray(certificateData?.assets) ? certificateData.assets : [];
  if (bundles.length > 0) {
    return bundles.map((backend, idx) => {
      const realizedYield = backend?.co2_realized;
      const bundledAmount = firstPresent(
        certificateType === "retirement"
          ? backend?.claimed_range_credits
          : backend?.no_of_entitlement_records,
        backend?.no_of_entitlement_records,
        backend?.co2_realized
      );
      const assetId = firstPresent(
        backend?.external_asset_id,
        backend?.asset_id
      );
      const period = backend?.period;
      const productVolume = backend?.yield_product_volume;
      const issuanceDate = firstPresent(backend?.created, backend?.issuanceDate);
      const tokenId = backend?.yield_token_id;

      return {
        key: `bundle-${idx}`,
        tokenId: isPresent(tokenId) ? `NFRY-${String(tokenId).trim()}` : "-",
        issuanceDate: isPresent(issuanceDate)
          ? formatCertificateTableDate(issuanceDate)
          : "-",
        assetId: isPresent(assetId) ? String(assetId) : "-",
        period: isPresent(period) ? String(period) : "-",
        productVolume: asTon(productVolume),
        yield: asTco2(realizedYield),
        bundledAmount: asTco2(bundledAmount),
      };
    });
  }

  const totalLegacyRealized = assets.reduce((sum, asset) => {
    const yields = Array.isArray(asset?.yields) ? asset.yields : [];
    return (
      sum +
      yields.reduce((yieldSum, yieldData) => {
        const realized = yieldData?.yield_co2_realized;
        return yieldSum + (isPresent(realized) ? Number(realized) : 0);
      }, 0)
    );
  }, 0);
  const rows = [];
  let rowIndex = 0;
  let bundleIndex = 0;

  for (const asset of assets) {
    const yields = Array.isArray(asset?.yields) ? asset.yields : [];
    for (const yieldData of yields) {
      const backend = bundleIndex < bundles.length ? bundles[bundleIndex++] : null;
      const realizedYield = firstPresent(backend?.co2_realized, yieldData?.yield_co2_realized);
      const bundledAmount = firstPresent(
        certificateType === "retirement"
          ? backend?.claimed_range_credits
          : backend?.no_of_entitlement_records,
        backend?.no_of_entitlement_records,
        computeFallbackBundledAmount(realizedYield, totalLegacyRealized)
      );
      const assetId = firstPresent(
        backend?.external_asset_id,
        asset?.external_asset_id,
        asset?.asset_name,
        asset?.asset_id,
        backend?.asset_id
      );
      const period = firstPresent(backend?.period, yieldData?.yield_period);
      const productVolume = firstPresent(backend?.yield_product_volume, null);
      const issuanceDate = firstPresent(backend?.created, backend?.issuanceDate);
      const tokenId = firstPresent(backend?.yield_token_id, null);

      rows.push({
        key: `legacy-${rowIndex++}`,
        tokenId: isPresent(tokenId) ? `NFRY-${String(tokenId).trim()}` : "-",
        issuanceDate: isPresent(issuanceDate)
          ? formatCertificateTableDate(issuanceDate)
          : "-",
        assetId: isPresent(assetId) ? String(assetId) : "-",
        period: isPresent(period) ? String(period) : "-",
        productVolume: asTon(productVolume),
        yield: asTco2(realizedYield),
        bundledAmount: asTco2(bundledAmount),
      });
    }
  }
  return rows;
}
